import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, User, Bell, TrendingUp } from 'lucide-react';
import PrayingHands from '@/components/icons/PrayingHands';

/**
 * BottomNav - Persistent bottom navigation for mobile/tablet
 * Shows: Home, Pray, Groups, Profile, Prayer is Rising, Notifications
 */
const BottomNav = ({ user, unreadCount = 0 }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  const navItems = [
    {
      path: '/requestor',
      label: 'Home',
      icon: Home,
      matchPaths: ['/requestor', '/request']
    },
    {
      path: '/pray-today',
      label: 'Pray',
      icon: null, // Custom icon
      customIcon: PrayingHands,
      matchPaths: ['/pray-today']
    },
    {
      path: '/communities',
      label: 'Groups',
      icon: Users,
      matchPaths: ['/communities', '/community', '/plan', '/pricing']
    },
    {
      path: '/prayer-rising',
      label: 'Rising',
      icon: TrendingUp,
      matchPaths: ['/prayer-rising']
    },
    {
      path: '/notifications',
      label: 'Alerts',
      icon: Bell,
      matchPaths: ['/notifications'],
      badge: unreadCount
    },
    {
      path: '/profile',
      label: 'Profile',
      icon: User,
      matchPaths: ['/profile']
    },
  ];
  
  const isActive = (item) => {
    return item.matchPaths.some(p => currentPath.startsWith(p));
  };
  
  if (!user) return null;
  
  // Don't show on onboarding, landing, or auth pages
  const hideOnPaths = ['/', '/onboarding', '/auth'];
  if (hideOnPaths.includes(currentPath)) return null;
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E8E6DF] z-50 md:hidden safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const active = isActive(item);
          const IconComponent = item.icon;
          const CustomIcon = item.customIcon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                active ? 'text-[#4E8B7C]' : 'text-[#8C9696]'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <div className="relative">
                {CustomIcon ? (
                  <CustomIcon 
                    size={22} 
                    fill={active ? '#4E8B7C' : '#8C9696'}
                    color={active ? '#4E8B7C' : '#8C9696'}
                  />
                ) : (
                  <IconComponent size={22} strokeWidth={active ? 2.5 : 2} />
                )}
                {item.badge > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span className={`text-[10px] mt-0.5 ${active ? 'font-medium' : ''}`}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
